import {
  ErrorResponse,
  instanceOfErrorListResponse,
  Service,
  ErrorListResponse,
} from '@genial/services';
import { ReportingEnumsProfitabilityReportMarkingTypeEnum } from '@genial/services/dist/models';
import { Utils } from '@genial/utils';
import { useCallback } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { useBase64Downloader } from '../../hooks/useBase64Downloader';
import { useToastCreator } from '../../hooks/useToastCreator';
import { advisorIdToApisSelector, partnerIdToApisSelector } from '../master/selector';

import { customerAssetProductSelected } from './atom';
import {
  updateCustomersAssetsDateSelected,
  updateIsExportingPdfForCustomerAssets,
} from './selector';
import { IInvestedAssetsNormalized } from './types';

export const useCustomerDetailsActions = () => {
  const { showToast } = useToastCreator('download-profitability-report-toast');
  const { downloadBase64 } = useBase64Downloader();
  const selectedAdvisor = useRecoilValue(advisorIdToApisSelector);
  const selectedPartner = useRecoilValue(partnerIdToApisSelector);

  const setIsExportingPdfForCustomerAssets = useSetRecoilState(
    updateIsExportingPdfForCustomerAssets
  );

  const setCustomersAssetsDateSelected = useSetRecoilState(updateCustomersAssetsDateSelected);
  const setCustomerAssetProductSelected = useSetRecoilState(customerAssetProductSelected);
  const setAssetsDetailProductSelected = useCallback((product: IInvestedAssetsNormalized) => {
    setCustomerAssetProductSelected((prev) => ({
      ...prev,
      [product.marketTypeNormalized]: product,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  interface RequestCostumerAssetsDetailPdf {
    consolidatedDate: Date;
    accountId: number;
    marketType?: string;
    sharingType?: 'Email' | 'Download';
  }
  interface RequestProfitabilityReport {
    customerId: number;
    accountNumber: number;
    profitabilityType: ReportingEnumsProfitabilityReportMarkingTypeEnum;
    initialMonth: string;
    initialYear: string;
  }

  const downloadCustomerAssetsDetailsPdf = async ({
    accountId,
    consolidatedDate,
    sharingType = 'Download',
    marketType = '',
  }: RequestCostumerAssetsDetailPdf) => {
    if (marketType) {
      return;
    }
    setIsExportingPdfForCustomerAssets(true);

    const handleDownloadError = (error?: ErrorResponse) => {
      showToast(
        'error',
        'Não foi possível processar sua solicitação.',
        error?.errorMessage || 'Não há informações disponíveis no momento.'
      );
    };

    try {
      const {
        data: { pdf, notificationSent },
      } = await Service.Finance.V1.getConsolidatedByDataPdfAccountId({
        accountId,
        consolidatedDate,
        sharingType,
        advisorId: selectedAdvisor,
        partnerId: selectedPartner,
        screenName: 'customerDetails',
      });

      setIsExportingPdfForCustomerAssets(false);

      if (pdf === '' && notificationSent) {
        showToast(
          'success',
          'Extrato enviado com sucesso.',
          'O extrato foi enviado para o email do cliente.'
        );
        return;
      }

      if (!pdf || pdf === '') {
        handleDownloadError();
        return;
      }

      const dateFormatted = Utils.Mask.DateFormat(consolidatedDate, 'yyyy-MM-dd');
      const fileName = `${accountId}_ativos_detalhados_${dateFormatted}`;

      await downloadBase64(pdf, fileName, 'pdf');
    } catch (error) {
      handleDownloadError(error as ErrorResponse);
      throw error;
    } finally {
      setIsExportingPdfForCustomerAssets(false);
    }
  };

  const downloadProfitabilityReport = async ({
    customerId,
    accountNumber,
    profitabilityType,
    initialMonth,
    initialYear,
  }: RequestProfitabilityReport) => {
    const handleDownloadError = (error?: ErrorResponse) => {
      showToast(
        'error',
        'Opa! Algo parece errado.',
        error?.errorMessage ||
          'Ocorreu um erro ao processar sua solicitação.\nAguarde um pouco e tente novamente.'
      );
    };

    try {
      const {
        data: { pdf },
      } = await Service.Reporting.V2.getProfitabilityReport({
        customerId,
        sinacorClientCode: accountNumber,
        markingType: profitabilityType,
        month: Number(initialMonth),
        year: Number(initialYear),
        advisorId: selectedAdvisor,
        partnerId: selectedPartner,
        origin: 'Partner',
        screenName: 'customerDetails',
      });

      if (!pdf || pdf === '') {
        handleDownloadError();
        return;
      }

      const fileName = `${accountNumber}_rentabilidade_${initialMonth}_${initialYear}`;

      await downloadBase64(pdf, fileName, 'pdf');
      showToast(
        'success',
        'Arquivo gerado com sucesso',
        'O arquivo foi gerado e o download será iniciado automaticamente.'
      );
    } catch (error) {
      if (instanceOfErrorListResponse(error as object)) {
        (error as ErrorListResponse).errors?.forEach((errorItem) => handleDownloadError(errorItem));
        return;
      }

      handleDownloadError();
    }
  };

  return {
    downloadCustomerAssetsDetailsPdf,
    setCustomersAssetsDateSelected,
    setAssetsDetailProductSelected,
    downloadProfitabilityReport,
  };
};
